<template>
  <v-dialog
    :value="value"
    max-width="760px"
    persistent
    @input="onDialogInput"
  >
    <v-card>
      <v-card-title>
        <span class="headline">Set Up a Workstation</span>
      </v-card-title>

      <v-card-text>
        <template v-if="!issuedCode">
          <p class="body-2">
            Describe the desk once here. You get a short setup code and an installer named for it;
            whoever is at that workstation only has to run the installer. The machine collects its
            own token, so nobody has to copy a long credential from one screen to another.
          </p>

          <v-text-field
            id="enrollLabel"
            v-model="label"
            label="Label (which machine is this?)"
            hint="e.g. Washington - front desk PC. This is what you will pick from when pausing or revoking."
            persistent-hint
            dense
          ></v-text-field>

          <v-select
            id="enrollScopes"
            v-model="scopes"
            :items="availableScopes"
            label="Scopes"
            hint="What this desk will be allowed to do. It can do nothing beyond them, and they cannot be widened later."
            persistent-hint
            multiple
            chips
            dense
            class="mt-4"
          ></v-select>

          <v-text-field
            id="enrollWkst"
            v-model="wkst"
            label="Workstation # (optional)"
            type="number"
            min="1"
            dense
            class="mt-4"
          ></v-text-field>

          <v-divider class="my-4"></v-divider>

          <div class="subtitle-2">Set this desk up automatically (optional)</div>
          <p class="caption mb-2">
            Which modules the desk should come up running. The folders and the poll interval are
            filled in at the desk, and are not cleared on a machine that already has them.
          </p>

          <v-checkbox
            id="enrollPullEnabled"
            v-model="config.pullEnabled"
            label="Turn on Pull from Cloud"
            dense
            hide-details
          ></v-checkbox>

          <v-checkbox
            id="enrollPushEnabled"
            v-model="config.pushEnabled"
            label="Turn on Push to Cloud"
            dense
            hide-details
            class="mt-2"
          ></v-checkbox>
        </template>

        <template v-else>
          <v-alert type="success" outlined dense>
            <div class="font-weight-bold mb-2">Setup code for &ldquo;{{ issuedLabel }}&rdquo;</div>
            <v-text-field
              id="enrollIssuedCode"
              :value="issuedCode"
              readonly
              dense
              hide-details
              class="mb-2 enroll-code"
            ></v-text-field>
            <v-btn small @click="copy(issuedCode, 'code')">Copy code</v-btn>
            <span v-if="copied === 'code'" class="ml-2 caption">Copied</span>
            <div class="caption mt-2">
              Good for {{ expiresIn }}. It can only set up one workstation, and it is spent the
              moment it does.
            </div>
            <div v-if="county" class="caption mt-2">
              If the installer below cannot be used, this is what gets typed at the desk, under
              <strong>Settings &rarr; General &rarr; Set up this workstation</strong>:
              <code class="enroll-line">{{ setupLine }}</code>
              <v-btn x-small text @click="copy(setupLine, 'line')">Copy</v-btn>
              <span v-if="copied === 'line'" class="ml-1">Copied</span>
            </div>
          </v-alert>

          <div class="mt-4">
            <v-btn
              id="enrollDownloadBtn"
              color="primary"
              :disabled="!county || downloading"
              :loading="downloading"
              @click="download"
            >
              Download installer for this desk
            </v-btn>
            <div v-if="downloading" class="caption mt-2">
              <v-progress-linear
                :value="progress || 0"
                :indeterminate="progress === null"
                height="6"
              ></v-progress-linear>
              Downloading{{ progress === null ? '' : ` ${progress}%` }} &mdash; this is a large file.
            </div>
            <div v-if="savedAs" class="caption mt-2">
              Saved as <strong>{{ savedAs }}</strong>. Run it on that workstation; it sets itself up.
            </div>
            <div v-if="!county" class="caption mt-2 orange--text">
              This page is not being served from a county address, so the installer cannot be named
              for a county. Use the code by hand instead.
            </div>
          </div>

          <v-alert v-if="downloadError" type="warning" outlined dense class="mt-4">
            {{ downloadError }}
            <div class="caption mt-2">
              Download the installer
              <a :href="plainInstallerUrl" target="_blank" rel="noopener">the ordinary way</a>,
              then type the code above into the toolkit's
              <strong>Settings &rarr; General &rarr; Set up this workstation</strong>.
            </div>
          </v-alert>
        </template>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text color="blue darken-1" @click="close">
          {{ issuedCode ? 'Done' : 'Cancel' }}
        </v-btn>
        <v-btn
          v-if="!issuedCode"
          id="enrollCreateBtn"
          text
          color="blue darken-1"
          @click="create"
        >
          Create
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { countySlug } from '../../util/clients/client-config';
import { TOOLKIT_FEED_URL, downloadToolkitInstaller } from '../../util/toolkit-download';

/**
 * Issue one workstation setup code, and hand back an installer named for it.
 *
 * This is the guided path; MachineTokenMintDialog is still the raw one, for a desk that already has
 * the toolkit installed or for somebody who wants the token itself. The difference that matters is
 * that here **no token is minted** - the code is a one-shot right to become a desk, and the
 * credential is generated by the auth service when the machine redeems it. So unlike a minted
 * token, losing this code costs nothing: cancel the enrollment and issue another.
 *
 * The code still lives only in this component's data and is never committed to Vuex, and
 * `persistent` is still deliberate, because reissuing is a small annoyance rather than free.
 *
 * Design: docs/plans/GUIDED-ENROLLMENT-PLAN.md in the regov-county-toolkit repo.
 */
export default {
  name: 'MachineTokenEnrollDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    label: '',
    scopes: [],
    wkst: '',
    config: {
      pullEnabled: true,
      pushEnabled: true,
    },
    issuedCode: '',
    issuedLabel: '',
    expiresAt: null,
    // Which of the two copy buttons was last used, or '' for neither.
    copied: '',
    downloading: false,
    progress: 0,
    savedAs: '',
    downloadError: '',
  }),
  computed: {
    availableScopes() {
      return this.$store.state.UserAdministration.machineTokenScopes || [];
    },
    county() {
      return countySlug();
    },
    plainInstallerUrl() {
      return `${TOOLKIT_FEED_URL}/latest.yml`;
    },
    /**
     * The county and the code in one string, because the code alone does not say
     * which county's auth service to redeem it against. The toolkit parses this, the
     * bare code and the installer's own filename all through one function, so a desk
     * that already knows its county can take any of the three.
     */
    setupLine() {
      return this.county ? `${this.county}-${this.issuedCode}` : this.issuedCode;
    },
    expiresIn() {
      if (!this.expiresAt) return 'a limited time';
      const hours = Math.round((new Date(this.expiresAt) - Date.now()) / 3600000);
      if (hours <= 0) return 'a few more minutes';
      return hours === 1 ? 'about an hour' : `about ${hours} hours`;
    },
  },
  watch: {
    /**
     * Every scope, selected, unless the admin takes some away. A desk that can do nothing is the
     * one outcome nobody wants from this form, and the scope list is short enough to read at a
     * glance before pressing Create.
     *
     * A watcher rather than a `data` default because the list is the server's, not ours:
     * MachineTokensList dispatches loadMachineTokenScopes on mount, so a copy taken in data() or
     * created() is taken before it arrives and stays empty for good.
     */
    availableScopes: {
      immediate: true,
      handler(list) {
        if (!this.scopes.length) this.scopes = [...list];
      },
    },
  },
  methods: {
    /**
     * The auth service still insists on a label - it is the only human-readable identity an
     * enrollment has, and what an admin picks from when cancelling a code nobody claimed. Create is
     * no longer gated on one, so name the desk here rather than let a click fail on a blank field.
     */
    defaultLabel(wkstNum) {
      return wkstNum > 0 ? `Workstation ${wkstNum}` : 'Unnamed workstation';
    },
    async create() {
      const wkstNum = parseInt(this.wkst, 10);
      const label = String(this.label).trim() || this.defaultLabel(wkstNum);
      const created = await this.$store.dispatch('UserAdministration/createMachineTokenEnrollment', {
        label,
        scopes: this.scopes,
        wkst: Number.isNaN(wkstNum) ? undefined : wkstNum,
        // Only the two switches. A folder and a poll interval are properties of the machine
        // rather than of the enrollment, so they are asked for once at the desk instead of
        // guessed at from here; every other field the auth service accepts is left absent, which
        // means "leave the desk's own" rather than "blank it".
        config: { ...this.config },
      });
      if (created && created.code) {
        this.issuedCode = created.code;
        this.issuedLabel = (created.enrollment && created.enrollment.label) || label;
        this.expiresAt = created.enrollment && created.enrollment.expiresAt;
        this.copied = '';
      }
    },
    copy(value, which) {
      // navigator.clipboard is absent on http origins and in older embedded browsers; the value
      // stays selectable on screen either way.
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value);
        this.copied = which;
      }
    },
    async download() {
      this.downloading = true;
      this.downloadError = '';
      this.savedAs = '';
      this.progress = 0;
      try {
        const result = await downloadToolkitInstaller({
          county: this.county,
          code: this.issuedCode,
          onProgress: (percent) => { this.progress = percent; },
        });
        this.savedAs = result.filename;
      } catch (e) {
        // Almost always a missing CORS rule on the artifact container. Saying so is better than a
        // spinner that stops, and the manual route below still works.
        this.downloadError = `The installer could not be downloaded from here. ${e.message || ''}`.trim();
        this.$store.dispatch('logException', e, { root: true });
      } finally {
        this.downloading = false;
      }
    },
    onDialogInput(isOpen) {
      if (!isOpen) this.close();
    },
    close() {
      this.label = '';
      // Back to the defaults a freshly opened dialog has, not to empty: the scope watcher only
      // fires when the server's list changes, so clearing this would leave the next enrollment
      // with no scopes at all.
      this.scopes = [...this.availableScopes];
      this.wkst = '';
      this.config = {
        pullEnabled: true,
        pushEnabled: true,
      };
      this.issuedCode = '';
      this.issuedLabel = '';
      this.expiresAt = null;
      this.copied = '';
      this.downloading = false;
      this.progress = 0;
      this.savedAs = '';
      this.downloadError = '';
      this.$emit('input', false);
    },
  },
};
</script>

<style scoped>
/* The code is read off this screen and typed at a desk, so it gets a font that tells 8 from B. */
.enroll-code >>> input {
  font-family: monospace;
  font-size: 1.25rem;
  letter-spacing: 0.08em;
}

.enroll-line {
  font-family: monospace;
}
</style>
